import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, User, Tag, FileText, Shield, Users } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { useAppStore } from '../contexts/AppStore';

export const ProjectDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { projects, ipAssets, stakeholders } = useAppStore();
  
  const project = projects.find(p => p.id === id);
  
  if (!project) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900">Proyecto no encontrado</h2>
        <Button onClick={() => navigate('/proyectos')} className="mt-4">
          Volver a Proyectos
        </Button>
      </div>
    );
  }
  
  // Buscar PI asociada
  const linkedIp = ipAssets.filter(ip => ip.linkedProjectId === project.id);
  
  // Stakeholders interesados (simulado - primeros 3)
  const interestedStakeholders = stakeholders.slice(0, 3);
  
  // Documentos simulados
  const documents = [
    { name: 'Contrato_NDA.pdf', type: 'PDF', size: '245 KB' },
    { name: 'Informe_Tecnico.docx', type: 'DOCX', size: '1.2 MB' },
    { name: 'Presentacion_Comercial.pptx', type: 'PPTX', size: '3.5 MB' },
    { name: 'Evaluacion_Patentabilidad.pdf', type: 'PDF', size: '890 KB' },
  ];
  
  const getStatusVariant = (status: typeof project.status) => {
    const variants = {
      'Declaración': 'info' as const,
      'Evaluación': 'warning' as const,
      'Protección': 'default' as const,
      'Negociación': 'warning' as const,
      'Licenciado': 'success' as const,
    };
    return variants[status];
  };
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Button
          variant="ghost"
          onClick={() => navigate('/proyectos')}
          className="mb-4 flex items-center space-x-2"
        >
          <ArrowLeft size={20} />
          <span>Volver a Proyectos</span>
        </Button>
        
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{project.title}</h1>
            <div className="flex items-center space-x-4 mt-2">
              <Badge variant={getStatusVariant(project.status)}>
                {project.status}
              </Badge>
              <span className="text-sm text-gray-600">
                ID: {project.id}
              </span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Contenido principal */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Columna principal */}
        <div className="lg:col-span-2 space-y-6">
          {/* Información general */}
          <Card>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Información General
            </h2>
            
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <User className="text-gray-400 mt-1" size={20} />
                <div>
                  <p className="text-sm font-medium text-gray-600">Inventor Principal</p>
                  <p className="text-base text-gray-900">{project.leadInventor}</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <Calendar className="text-gray-400 mt-1" size={20} />
                <div>
                  <p className="text-sm font-medium text-gray-600">Fecha de Creación</p>
                  <p className="text-base text-gray-900">
                    {format(new Date(project.createdDate), "dd 'de' MMMM 'de' yyyy", { locale: es })}
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <Tag className="text-gray-400 mt-1" size={20} />
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-2">Tecnologías</p>
                  <div className="flex flex-wrap gap-2">
                    {project.technologies.map((tech, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              
              {project.description && (
                <div className="pt-4 border-t border-gray-200">
                  <p className="text-sm font-medium text-gray-600 mb-2">Descripción</p>
                  <p className="text-base text-gray-700 leading-relaxed">
                    {project.description}
                  </p>
                </div>
              )}
            </div>
          </Card>
          
          {/* Repositorio Documental */}
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">
                Repositorio Documental
              </h2>
              <Button variant="secondary" size="sm">
                Subir Archivo
              </Button>
            </div>
            
            <div className="space-y-2">
              {documents.map((doc, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                >
                  <div className="flex items-center space-x-3">
                    <FileText className="text-blue-600" size={24} />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{doc.name}</p>
                      <p className="text-xs text-gray-600">{doc.type} • {doc.size}</p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm">
                    Descargar
                  </Button>
                </div>
              ))}
            </div>
          </Card>
        </div>
        
        {/* Columna lateral */}
        <div className="space-y-6">
          {/* PI Asociada */}
          <Card>
            <div className="flex items-center space-x-2 mb-4">
              <Shield className="text-blue-600" size={24} />
              <h2 className="text-xl font-semibold text-gray-900">
                PI Asociada
              </h2>
            </div>
            
            {linkedIp.length > 0 ? (
              <div className="space-y-3">
                {linkedIp.map((ip) => (
                  <div key={ip.id} className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <Badge variant={ip.status === 'Concedida' ? 'success' : 'warning'}>
                        {ip.status}
                      </Badge>
                      <span className="text-xs text-gray-600">{ip.type}</span>
                    </div>
                    <p className="text-sm font-medium text-gray-900">{ip.title}</p>
                    <p className="text-xs text-gray-600 mt-1">
                      Vence: {format(new Date(ip.criticalDate), 'dd MMM yyyy', { locale: es })}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-600">No hay PI asociada a este proyecto</p>
            )}
          </Card>
          
          {/* Stakeholders Interesados */}
          <Card>
            <div className="flex items-center space-x-2 mb-4">
              <Users className="text-purple-600" size={24} />
              <h2 className="text-xl font-semibold text-gray-900">
                Stakeholders Interesados
              </h2>
            </div>
            
            <div className="space-y-3">
              {interestedStakeholders.map((stakeholder) => (
                <div key={stakeholder.id} className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm font-medium text-gray-900">{stakeholder.name}</p>
                  <p className="text-xs text-gray-600 mt-1">{stakeholder.type}</p>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {stakeholder.sectors.slice(0, 2).map((sector, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-purple-100 text-purple-800"
                      >
                        {sector}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};
